"use client";

import { useState } from "react";
import Link from "next/link";

import { LookupHistory } from "@/components/lookup-history";
import { RulesLookup } from "@/components/rules-lookup";
import { SavedAddresses } from "@/components/saved-addresses";
import { useAuth } from "@/hooks/use-auth";
import { useSubscription } from "@/hooks/use-subscription";

export default function DashboardPage() {
  const { user } = useAuth();
  const { data } = useSubscription();
  const entitled = Boolean(data?.active);
  const isPro = entitled && data?.plan === "pro";
  const [savedRefresh, setSavedRefresh] = useState(0);
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [prefill, setPrefill] = useState<{ address: string; topic: string; key: number }>({
    address: "",
    topic: "",
    key: 0,
  });

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-12 sm:px-6">
      <div className="mb-8">
        <p className="overline">Rules lookup</p>
        <h1 className="mt-2 font-serif text-display-sm font-semibold text-ink">
          What governs this address?
        </h1>
        <p className="mt-3 max-w-prose text-body-lg text-muted">
          Enter a US address and a topic. Prosa resolves every governing
          jurisdiction — federal down to local — and retrieves the operative
          rules live from official sources, quoted verbatim with citations.
        </p>
        {user?.email ? (
          <p className="mt-2 text-body-sm text-muted">Signed in as {user.email}</p>
        ) : null}
      </div>

      {data?.status === "expired" ? (
        // A trial that lapses must say so — before this, features silently
        // vanished with no explanation anywhere on the page.
        <div className="mb-6 border-l-2 border-primary/60 pl-3">
          <p className="max-w-prose text-body-sm text-muted">
            Your free trial has ended. Lookups still work; plain-language
            explanations, saved addresses with monitoring, and reports are
            part of the{" "}
            <Link href="/pricing" className="text-primary hover:underline">
              paid plans
            </Link>
            .
          </p>
        </div>
      ) : null}

      <RulesLookup
        key={prefill.key}
        entitled={entitled}
        canSave={entitled}
        initialAddress={prefill.address}
        initialTopic={prefill.topic}
        onSaved={() => setSavedRefresh((n) => n + 1)}
        onCompleted={user ? () => setHistoryRefresh((n) => n + 1) : undefined}
      />

      <div className="mt-10 space-y-5">
        <SavedAddresses refreshKey={savedRefresh} />
        <LookupHistory
          refreshKey={historyRefresh}
          onRerun={(address, topic) =>
            setPrefill((p) => ({ address, topic, key: p.key + 1 }))
          }
        />
      </div>

      <p className="mt-8 text-body-sm text-muted">
        {isPro ? (
          <>
            Need many addresses at once?{" "}
            <Link href="/bulk" className="text-primary hover:underline">
              Bulk lookup
            </Link>
            .
          </>
        ) : (
          <>
            Need monitoring across a portfolio, bulk lookups, or client-ready
            reports?{" "}
            <Link href="/pricing" className="text-primary hover:underline">
              See plans
            </Link>
            .
          </>
        )}
      </p>
    </div>
  );
}
